import { useTheme, useMediaQuery } from '@mui/material';
import { useState, useEffect } from 'react';

/**
 * useResponsive - Comprehensive responsive utilities hook
 * 
 * Provides Material-UI breakpoint utilities and responsive state management
 * with optimized performance and touch device detection.
 * 
 * Features:
 * - Material-UI breakpoint queries
 * - Touch device detection
 * - Screen orientation tracking
 * - Performance optimized with reduced re-renders
 * - Waltero-specific responsive utilities
 */

export const useResponsive = () => {
  const theme = useTheme();
  
  // Material-UI Breakpoint Queries
  const isXs = useMediaQuery(theme.breakpoints.only('xs')); // 0-599px (mobile)
  const isSm = useMediaQuery(theme.breakpoints.only('sm')); // 600-959px (tablet)
  const isMd = useMediaQuery(theme.breakpoints.only('md')); // 960-1279px (desktop)
  const isLg = useMediaQuery(theme.breakpoints.only('lg')); // 1280-1919px (large desktop)
  const isXl = useMediaQuery(theme.breakpoints.only('xl')); // 1920px+ (extra large)
  
  // Range Queries
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // xs + sm (0-959px)
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md')); // sm only
  const isDesktop = useMediaQuery(theme.breakpoints.up('md')); // md + lg + xl (960px+)
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg')); // lg + xl (1280px+)
  
  // Custom Waltero Breakpoints
  const isTouchDevice = useMediaQuery('(hover: none) and (pointer: coarse)');
  const isSmallMobile = useMediaQuery('(max-width: 480px)');
  const isLandscape = useMediaQuery('(orientation: landscape)');
  const isPortrait = useMediaQuery('(orientation: portrait)');
  
  // Screen size state
  const [screenSize, setScreenSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0
  });
  
  // Track screen size changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Responsive navigation configuration
  const getNavigationConfig = () => {
    if (isMobile) {
      return {
        variant: 'temporary',
        drawerWidth: Math.min(280, screenSize.width * 0.85),
        showMenuButton: true,
        autoClose: true,
        swipeAreaWidth: 20
      };
    }
    
    if (isTablet) {
      return {
        variant: 'persistent',
        drawerWidth: 240,
        showMenuButton: true,
        autoClose: false,
        swipeAreaWidth: 0
      };
    }
    
    return {
      variant: 'permanent',
      drawerWidth: 280,
      showMenuButton: false,
      autoClose: false,
      swipeAreaWidth: 0
    };
  };
  
  // Touch-friendly spacing and sizing
  const getTouchConfig = () => ({
    minTouchTarget: isTouchDevice ? 44 : 32, // 44px minimum for touch accessibility
    spacing: isMobile ? theme.spacing(1) : theme.spacing(2),
    buttonSize: isTouchDevice ? 'large' : 'medium',
    iconSize: isTouchDevice ? 'large' : 'medium',
    tableDensity: isMobile ? 'normal' : 'compact'
  });
  
  // Responsive chart configuration
  const getChartConfig = () => ({
    height: isMobile ? 250 : isTablet ? 300 : 400,
    responsive: true,
    maintainAspectRatio: !isMobile,
    legend: {
      display: !isSmallMobile,
      position: isMobile ? 'bottom' : 'top'
    },
    scales: {
      x: {
        ticks: {
          maxTicksLimit: isMobile ? 5 : isTablet ? 8 : 12
        }
      },
      y: {
        ticks: {
          maxTicksLimit: isMobile ? 5 : 8
        }
      }
    }
  });
  
  // Grid system configuration
  const getGridConfig = () => ({
    container: {
      spacing: isMobile ? 1 : 2,
      direction: isMobile && isPortrait ? 'column' : 'row'
    },
    item: {
      xs: 12,
      sm: isMobile ? 12 : 6,
      md: 6,
      lg: 4,
      xl: 3
    }
  });
  
  // Table configuration for mobile
  const getTableConfig = () => ({
    size: isMobile ? 'small' : 'medium',
    stickyHeader: true,
    pagination: {
      rowsPerPageOptions: isMobile ? [5, 10] : [10, 25, 50],
      rowsPerPage: isMobile ? 5 : 10,
      showFirstButton: !isMobile,
      showLastButton: !isMobile
    }
  });
  
  return {
    // Breakpoint states
    breakpoints: {
      isXs,
      isSm,
      isMd,
      isLg,
      isXl,
      isMobile,
      isTablet,
      isDesktop,
      isLargeScreen,
      isSmallMobile
    },
    
    // Device capabilities
    device: {
      isTouchDevice,
      isLandscape,
      isPortrait,
      screenSize
    },
    
    // Configuration helpers
    navigation: getNavigationConfig(),
    touch: getTouchConfig(),
    chart: getChartConfig(),
    grid: getGridConfig(),
    table: getTableConfig(),
    
    // Utility functions
    utils: {
      // Get responsive value based on breakpoint
      getResponsiveValue: (values) => {
        const { xs, sm, md, lg, xl } = values;
        if (isXl && xl !== undefined) return xl;
        if (isLg && lg !== undefined) return lg;
        if (isMd && md !== undefined) return md;
        if (isSm && sm !== undefined) return sm;
        return xs;
      },
      
      // Get spacing based on screen size
      getSpacing: (base = 1) => {
        if (isMobile) return theme.spacing(base);
        if (isTablet) return theme.spacing(base * 1.5);
        return theme.spacing(base * 2);
      },
      
      // Get font size based on screen
      getFontSize: (base) => {
        if (isMobile) return base * 0.875;
        if (isTablet) return base * 0.9375;
        return base;
      }
    }
  };
};

/**
 * useBreakpoint - Simplified breakpoint hook
 * Returns current breakpoint name as string
 */
export const useBreakpoint = () => {
  const { breakpoints } = useResponsive();
  
  if (breakpoints.isXs) return 'xs';
  if (breakpoints.isSm) return 'sm';
  if (breakpoints.isMd) return 'md';
  if (breakpoints.isLg) return 'lg';
  if (breakpoints.isXl) return 'xl';
  
  return 'md'; // fallback
};

/**
 * useIsMobile - Simple mobile detection hook
 */
export const useIsMobile = () => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('md'));
};

/**
 * useTouchDevice - Touch device detection hook
 */
export const useTouchDevice = () => {
  return useMediaQuery('(hover: none) and (pointer: coarse)');
};

export default useResponsive;